import _ from 'lodash';
import moment from 'moment';
import { Fragment, useCallback, useState } from 'react';

import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { useGetReferenceRequestsByApplicationQuery, useResendReferenceRequestMutation } from 'src/services/references/references.service';
import { ReferenceRequestResponse, ReferenceRequestStatus } from 'src/services/references/references.types';
import { useAppDispatch } from 'src/store/store';

import EmptyContent from 'src/components/empty-content';
import Label from 'src/components/label';

import ReviewRequestModal from 'src/sections/jobs/applications/modals/references/review-request-modal';
import SubmitRefereeModal from 'src/sections/jobs/applications/modals/references/submit-referee-modal';

import Iconify from 'src/components/iconify';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useGetApplicationByIdQuery } from 'src/services/applications/applications.service';
import { rootApi } from 'src/services/rootApi';
import { TenantType } from 'src/types/enums';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { Box } from '@mui/system';
import SubmitReferenceModal from './submit-reference-modal';

type Props = {
  open: boolean;
  applicationId: string;
  onClose: VoidFunction;
};

enum TabValue {
  PENDING = 'pending',
  COMPLETED = 'completed'
}

export default function ReferenceRequestListModal({ open, applicationId, onClose }: Props) {
  const { t } = useTranslate();

  const actionsPopover = usePopover();

  const tenantType = useOrgTenant();
  const authContext = useAuthContext();

  const dispatch = useAppDispatch();

  const submitRefereeModalControl = useBoolean();
  const reviewRequestModalControl = useBoolean();
  const submitReferenceModalControl = useBoolean();

  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);

  const [selectedTab, setSelectedTab] = useState<TabValue>(TabValue.PENDING);

  const [pageOptions, setPageOptions] = useState<PageOptions>({
    page: 1,
    per_page: 5,
  });

  const [resendRequest, { isLoading: isResending }] = useResendReferenceRequestMutation();

  const { currentData: application } = useGetApplicationByIdQuery(applicationId, {
    skip: !applicationId
  });

  const { currentData: referenceRequests } = useGetReferenceRequestsByApplicationQuery({
    applicationId,
    status: selectedTab as TabValue,
    params: pageOptions,
  });

  const onChangePage = useCallback((value: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: value,
    }));
  }, []);

  const getStatusLabelText = (request: ReferenceRequestResponse) => {
    switch (request.status) {
      case ReferenceRequestStatus.PENDING_APPROVAL:
        return t('applications.references.request_list_modal.status.pending_approval');
      case ReferenceRequestStatus.PENDING_REFEREES:
        return t('applications.references.request_list_modal.status.pending_referees');
      case ReferenceRequestStatus.PENDING_REFERENCES:
        return t('applications.references.request_list_modal.status.pending_references');
      case ReferenceRequestStatus.COMPLETED:
        return t('applications.references.request_list_modal.status.completed');
      case ReferenceRequestStatus.EXPIRED:
        return t('applications.references.request_list_modal.status.expired');
      case ReferenceRequestStatus.REJECTED:
        return t('applications.references.request_list_modal.status.rejected');
      default:
        return '';
    }
  };

  const onSubmitReferee = useCallback((referenceRequestId: string) => {
    setSelectedRequest(referenceRequestId);
    submitRefereeModalControl.onTrue();
  }, [submitRefereeModalControl]);

  const onCloseSubmitRefereeModal = useCallback(() => {
    setSelectedRequest(null);
    submitRefereeModalControl.onFalse();
  }, [submitRefereeModalControl]);

  const onReviewRequest = useCallback((referenceRequestId: string) => {
    setSelectedRequest(referenceRequestId);
    reviewRequestModalControl.onTrue();
  }, [reviewRequestModalControl]);

  const onSubmitManualReference = useCallback((referenceRequestId: string) => {
    setSelectedRequest(referenceRequestId);
    submitReferenceModalControl.onTrue();
  }, [submitReferenceModalControl]);

  const onCloseReviewRequestModal = useCallback(() => {
    setSelectedRequest(null);
    reviewRequestModalControl.onFalse();
  }, [reviewRequestModalControl]);

  const onCloseSubmitReferenceModal = useCallback(() => {
    dispatch(rootApi.util.invalidateTags([{ type: 'ApplicationReferenceRequests', id: applicationId }]));
    setSelectedRequest(null);
    submitReferenceModalControl.onFalse();
  }, [submitReferenceModalControl, dispatch, applicationId]);

  const handleResendRequest = useCallback(async (referenceRequestId: string) => {
    const toastId = enqueueSnackbar(t('applications.references.request_list_modal.api.resend.loading'), {
      variant: 'loading',
      persist: true
    });

    try {
      await resendRequest({
        applicationId,
        referenceRequestId
      }).unwrap();

      closeSnackbar(toastId);
      enqueueSnackbar(t('applications.references.request_list_modal.api.resend.success'), {
        variant: 'success'
      });
    }
    catch (error) {
      closeSnackbar(toastId);
      enqueueSnackbar(t('applications.references.request_list_modal.api.resend.default_error'), { variant: 'error' });
    }

  }, [resendRequest, applicationId, t]);

  const renderViewedLabel = (request: ReferenceRequestResponse) => {
    if (request.status === ReferenceRequestStatus.PENDING_REFERENCES) {
      if (request.referee_viewed_at) {
        return (
          <Tooltip title={t('applications.references.request_list_modal.labels.viewed_tooltip', { date: new Date(request.referee_viewed_at) })}>
            <Iconify icon="eva:eye-fill" width={20} height={20} color="green" sx={{ cursor: 'help' }} />
          </Tooltip>
        );
      }

      return (
        <Tooltip title={t('applications.references.request_list_modal.labels.not_viewed_tooltip')}>
          <Iconify icon="eva:eye-off-fill" width={20} height={20} color="grey" sx={{ cursor: 'help' }} />
        </Tooltip>
      );

    }

    return null;
  };

  const renderActionButton = useCallback((request: ReferenceRequestResponse) => {

    if (!application) {
      return null;
    }

    if (request.status === ReferenceRequestStatus.PENDING_APPROVAL && tenantType === TenantType.Recruiter) {
      return (
        <Button
          variant="outlined"
          color="primary"
          onClick={() => onReviewRequest(request.id)}
          size='small'
        >
          {t('common.review')}
        </Button>
      );
    }

    if (request.status === ReferenceRequestStatus.PENDING_REFERENCES && application.manager.id === authContext.organization?.id) {
      return (
        <>
          <IconButton
            color={actionsPopover.open ? 'inherit' : 'default'}
            onClick={actionsPopover.onOpen}
          >
            <Iconify icon="eva:more-horizontal-fill" />
          </IconButton>

          <CustomPopover
            open={actionsPopover.open}
            onClose={actionsPopover.onClose}
            arrow="right-top"
            sx={{ width: 200 }}
          >
            <MenuItem
              onClick={() => {
                actionsPopover.onClose();
                onSubmitManualReference(request.id)
              }}
            >
              <Iconify icon="mdi:eye" />
              <Typography variant='body2'>{t('common.view')}</Typography>
            </MenuItem>

            <MenuItem
              onClick={() => {
                actionsPopover.onClose();
                handleResendRequest(request.id)
              }}
            >
              <Iconify icon="mdi:email-resend" />
              <Typography variant='body2'>{t('applications.references.request_list_modal.actions.resend_request')}</Typography>
            </MenuItem>

          </CustomPopover>
        </>
      );
    }

    if (request.status === ReferenceRequestStatus.PENDING_REFEREES && tenantType === TenantType.Candidate) {
      return (
        <Button
          variant="contained"
          color="primary"
          size='small'
          onClick={() => onSubmitReferee(request.id)}
        >
          {t('applications.references.request_list_modal.labels.submit_referee')}
        </Button>
      );
    }

    return null;
  }, [tenantType, onSubmitReferee, onReviewRequest, t, onSubmitManualReference, application, authContext.organization, handleResendRequest, actionsPopover]);

  const changeTab = (_e: React.SyntheticEvent, value: TabValue) => {
    setSelectedTab(value);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('applications.references.request_list_modal.title')}</DialogTitle>
      <DialogContent>
        {
          (!!referenceRequests?.count && tenantType === TenantType.Candidate) && (
            <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
              <Alert severity="info">
                <AlertTitle>
                  <Typography variant="subtitle2">
                    {t('applications.references.request_list_modal.labels.notes_title')}
                  </Typography>
                </AlertTitle>

                <Typography variant="body2">
                  {_.capitalize(referenceRequests.results[0]?.notes)}
                </Typography>

              </Alert>
            </Stack>
          )
        }

        <Tabs
          value={selectedTab}
          onChange={changeTab}
          aria-label="Reference request tabs"
          role="navigation"
        >
          <Tab label={t('applications.references.request_list_modal.tabs.pending')} value={TabValue.PENDING} />
          <Tab label={t('applications.references.request_list_modal.tabs.completed')} value={TabValue.COMPLETED} />
        </Tabs>

        <Stack
          direction="column"
          sx={{ minHeight: 400, height: '100%', typography: 'body1', mt: 2 }}
          justifyContent="space-between"
        >
          {referenceRequests?.count === 0 ? (
            <EmptyContent
              filled
              title={t('applications.references.request_list_modal.no_requests')}
              sx={{
                py: 10,
              }}
            />
          ) : (
            <List sx={{ paddingBottom: 2, maxWidth: '100%' }}>
              {referenceRequests?.results.map((referenceRequest) => (
                <Fragment key={referenceRequest.id}>
                  <ListItem
                    sx={{ paddingY: 1 }}
                    secondaryAction={
                      <Stack direction="row" spacing={1} alignItems="center">
                        {renderViewedLabel(referenceRequest)}
                        {renderActionButton(referenceRequest)}
                      </Stack>
                    }
                  >
                    <ListItemText primary={
                      <Typography variant="subtitle2">
                        {referenceRequest.referee ? `${referenceRequest.referee.first_name} ${referenceRequest.referee.last_name}` : 'New Reference Request'}
                      </Typography>
                    } secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="textSecondary">
                          {moment(referenceRequest.created_at).format('DD/MM/YYYY hh:mm A')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          •
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {getStatusLabelText(referenceRequest)}
                        </Typography>
                      </Stack>
                    } />
                  </ListItem>
                  <Divider />
                </Fragment>
              ))}
            </List>
          )}

          <Stack flexDirection="row" justifyContent="center" sx={{ py: 2 }}>
            <Pagination
              page={pageOptions.page || 1}
              count={Math.ceil((referenceRequests?.count || 1) / pageOptions.per_page)}
              onChange={(_e, value) => onChangePage(value)}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>

      {
        submitRefereeModalControl.value && (
          <SubmitRefereeModal
            open={submitRefereeModalControl.value}
            referenceRequestId={selectedRequest as string}
            onClose={onCloseSubmitRefereeModal}
          />
        )
      }

      {
        reviewRequestModalControl.value && (
          <ReviewRequestModal
            open={reviewRequestModalControl.value}
            referenceRequestId={selectedRequest as string}
            onClose={onCloseReviewRequestModal}
          />
        )
      }

      {
        submitReferenceModalControl.value && (
          <SubmitReferenceModal
            open={submitReferenceModalControl.value}
            referenceRequestId={selectedRequest as string}
            onClose={onCloseSubmitReferenceModal}
          />
        )
      }


    </Dialog>
  );
}
